import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminAlertText,
  AdminRowMenuItem,
  AdminPageTitle,
  AdminRowAction,
  AdminRowActionsButton,
  AdminRowAvatar,
  AdminSearchField,
  AdminSortableTableHeader,
  AdminSupportingText,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminFixedRowActionsMenu,
  QUOTE_DISPLAY_STATUSES,
  messageThreadStatusBadgeClass,
  normalizeQuoteStatus,
  normalizeQuoteThreadDeliveryStatus,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { DashboardStatusLabel } from '../../../components/common/Dashboard';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { getQuotesForScenarios } from '../../../data/quoteModerationScenarios';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';

/** Quote ID | Account | Latest Thread | Threads | Status | Flags | Status | Last Quote On | Action */
const QUOTES_LIST_GRID =
  'minmax(96px, 0.85fr) minmax(110px, 1.1fr) minmax(140px, 1.6fr) minmax(64px, 0.55fr) minmax(88px, 0.75fr) minmax(56px, 0.45fr) minmax(100px, 0.9fr) minmax(120px, 1fr) minmax(56px, 0.45fr)';

const QUOTE_STATUS_FILTER_OPTIONS = [
  { value: null, label: 'All' },
  ...QUOTE_DISPLAY_STATUSES.map((status) => ({ value: status, label: status })),
];

const QUOTE_FLAG_FILTER_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'No Flags', label: 'No Flags' },
  { value: 'Has Flags', label: 'Has Flags' },
];

/** Total User Flags count for the parent Quote. */
function getFlagsCount(quote) {
  if (!quote) return 0;
  if (typeof quote.flagsCount === 'number') return quote.flagsCount;
  if (Array.isArray(quote.flags)) return quote.flags.length;
  if (quote.flagStatus && quote.flagStatus !== 'None') return 1;
  return 0;
}

/** Operational Status: Active | Under Review | Blocked | Restricted. */
function getQuoteRecordStatus(quote) {
  if (!quote) return 'Active';
  return normalizeQuoteStatus(quote.status || quote.listStatus || quote.quoteStatus, quote.messagingState, {
    underReview: quote.underReview,
    caseStatus: quote.caseStatus,
    caseSeverity: quote.caseSeverity,
    messagingState: quote.messagingState,
  });
}

function getLatestQuoteThreadStatus(quote) {
  return normalizeQuoteThreadDeliveryStatus(quote?.latestQuoteThreadStatus || quote?.latestMessageStatus);
}

function quoteThreadStatusTextClass(status) {
  if (status === 'Read') return 'text-freshgreen';
  if (status === 'Delivered') return 'text-primaryblue';
  if (status === 'Sent') return 'text-warmyellow';
  return '';
}

function matchesQuoteSearch(quote, searchLower) {
  if (!searchLower) return true;

  const quoteId = (quote.quoteId || quote.threadId || quote.conversationId || '').toLowerCase();
  const quoteRequestId = (quote.quoteRequestId || '').toLowerCase();
  const recipientMatchId = (quote.recipientMatchId || '').toLowerCase();
  const customerName = (quote.customer?.name || '').toLowerCase();
  const customerId = (quote.customer?.id || '').toLowerCase();
  const businessName = (quote.business?.name || '').toLowerCase();
  const businessId = (quote.business?.id || '').toLowerCase();
  const flagIds = (quote.flags || []).map((f) => String(f.flagId || '').toLowerCase());
  const caseId = (quote.moderationCaseId || '').toString().toLowerCase();
  const caseIds = (quote.moderationCases || []).map((c) => String(c.caseId || '').toLowerCase());
  const threadIds = (quote.quoteThreadIds || []).map((id) => String(id).toLowerCase());

  return (
    quoteId.includes(searchLower) ||
    quoteRequestId.includes(searchLower) ||
    recipientMatchId.includes(searchLower) ||
    customerName.includes(searchLower) ||
    customerId.includes(searchLower) ||
    businessName.includes(searchLower) ||
    businessId.includes(searchLower) ||
    flagIds.some((id) => id.includes(searchLower)) ||
    caseId.includes(searchLower) ||
    caseIds.some((id) => id.includes(searchLower)) ||
    threadIds.some((id) => id.includes(searchLower))
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const Quotes = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    status: null,
    flag: 'All',
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const statusRef = useRef(null);
  const flagRef = useRef(null);

  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(flagRef, () => setFlagOpen(false));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [sortField, setSortField] = useState('lastQuoteThreadOn');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.SINGLE);

  // Deep-link from Business or Customer profile
  useEffect(() => {
    const linkedBusinessId = location.state?.businessId;
    const linkedCustomerId = location.state?.customerId;
    if (linkedBusinessId) {
      setSearchQuery(String(linkedBusinessId));
      setDebouncedSearch(String(linkedBusinessId));
    } else if (linkedCustomerId) {
      setSearchQuery(String(linkedCustomerId));
      setDebouncedSearch(String(linkedCustomerId));
    }
  }, [location.state]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 400));
        let filtered = getQuotesForScenarios();

        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase().trim();
          filtered = filtered.filter((q) => matchesQuoteSearch(q, searchLower));
        }

        if (filters.status) {
          filtered = filtered.filter((q) => getQuoteRecordStatus(q) === filters.status);
        }
        if (filters.flag === 'Has Flags') {
          filtered = filtered.filter((q) => getFlagsCount(q) > 0);
        } else if (filters.flag === 'No Flags') {
          filtered = filtered.filter((q) => getFlagsCount(q) === 0);
        }

        filtered.sort((a, b) => {
          let aVal;
          let bVal;
          if (sortField === 'lastQuoteThreadOn' || sortField === 'lastActivity') {
            aVal = new Date(a.lastQuoteThreadOn || a.lastActivity).getTime();
            bVal = new Date(b.lastQuoteThreadOn || b.lastActivity).getTime();
          } else if (sortField === 'status') {
            aVal = getQuoteRecordStatus(a).toLowerCase();
            bVal = getQuoteRecordStatus(b).toLowerCase();
          } else if (sortField === 'customer') {
            aVal = (a.customer?.name || '').toLowerCase();
            bVal = (b.customer?.name || '').toLowerCase();
          } else if (sortField === 'business') {
            aVal = (a.business?.name || '').toLowerCase();
            bVal = (b.business?.name || '').toLowerCase();
          } else {
            aVal = a[sortField];
            bVal = b[sortField];
          }

          if (typeof aVal === 'string') {
            return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
          }
          if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });

        const total = filtered.length;
        const pages = Math.max(1, Math.ceil(total / CONFIG_LIST_PAGE_SIZE));
        const clampedPage = Math.min(currentPage, pages);
        const startIndex = (clampedPage - 1) * CONFIG_LIST_PAGE_SIZE;

        setTotalFilteredCount(total);
        setTotalPages(pages);
        setRows(filtered.slice(startIndex, startIndex + CONFIG_LIST_PAGE_SIZE));

        if (clampedPage !== currentPage) {
          setCurrentPage(clampedPage);
        }
        setError(null);
      } catch (err) {
        setError('Failed to load quotes');
        setRows([]);
        setTotalFilteredCount(0);
        setTotalPages(1);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRows();
  }, [debouncedSearch, filters, currentPage, sortField, sortOrder]);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const goQuoteDetail = (q) => {
    navigate(`/admin/quotes/all/${q.threadId || q.quoteId || q.id}`, {
      state: { from: location.pathname, message: q },
    });
  };

  const effectivePage = Math.min(currentPage, totalPages);
  const rangeStart =
    totalFilteredCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalFilteredCount);
  const showPagination = totalFilteredCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Quotes</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Quote ID, Quote Request ID, Quote Thread ID, Customer, Business, Flag, Case"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search quotes"
            />

            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={QUOTE_STATUS_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('status', value)}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Flags"
                value={filters.flag}
                options={QUOTE_FLAG_FILTER_OPTIONS}
                onChange={(value) => handleFilterChange('flag', value)}
                menuRef={flagRef}
                open={flagOpen}
                setOpen={setFlagOpen}
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-white animate-pulse rounded" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <AdminAlertText className="text-alertred mb-4 block">{error}</AdminAlertText>
              <AdminRowAction onClick={() => window.location.reload()}>Retry</AdminRowAction>
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10">
              <AdminSupportingText className="text-mediumgray">
                No quotes match your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <div ref={tableScrollRef} className="overflow-x-auto">
              <div className="min-w-[1100px]">
                <div
                  className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3"
                  style={{ gridTemplateColumns: QUOTES_LIST_GRID }}
                >
                  <div className="flex items-center">
                    <AdminTableHeader>Quote ID</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Account</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Latest Thread</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Threads</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Status</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminTableHeader>Flags</AdminTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminSortableTableHeader onClick={() => handleSort('status')}>Status</AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center">
                    <AdminSortableTableHeader onClick={() => handleSort('lastQuoteThreadOn')}>
                      Last Quote On
                    </AdminSortableTableHeader>
                  </div>
                  <div className="flex items-center justify-center">
                    <AdminTableHeader>Action</AdminTableHeader>
                  </div>
                </div>

                {rows.map((q, index) => {
                  const flagsCount = getFlagsCount(q);
                  const recordStatus = getQuoteRecordStatus(q);
                  const threadStatus = getLatestQuoteThreadStatus(q);
                  const quoteId = q.quoteId || q.threadId || q.id;
                  const rowKey = quoteId || `idx-${index}`;
                  const latestPreview = q.latestQuoteThreadPreview || q.latestMessagePreview;

                  return (
                    <div
                      key={rowKey}
                      role="button"
                      tabIndex={0}
                      onClick={() => goQuoteDetail(q)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') goQuoteDetail(q);
                      }}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3 cursor-pointer"
                      style={{ gridTemplateColumns: QUOTES_LIST_GRID }}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={quoteId}>
                          {quoteId || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={q.customer?.avatar || require('../../../assets/img/User.png')}
                          alt={q.customer?.name}
                          onError={(e) => {
                            e.target.src = require('../../../assets/img/User.png');
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={q.customer?.name}>
                          {q.customer?.name || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={latestPreview}>
                          {latestPreview || '—'}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center">
                        <AdminTableBody className="tabular-nums">
                          {q.totalQuoteThreads ?? q.messageCount ?? 0}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableBody className={`truncate ${quoteThreadStatusTextClass(threadStatus)}`.trim()}>
                          {threadStatus}
                        </AdminTableBody>
                      </div>

                      <div className="flex items-center">
                        <AdminTableBody className="tabular-nums">{flagsCount}</AdminTableBody>
                      </div>

                      <div className="flex items-center min-w-0">
                        <DashboardStatusLabel
                          className={`whitespace-nowrap ${messageThreadStatusBadgeClass(recordStatus)}`}
                        >
                          {recordStatus}
                        </DashboardStatusLabel>
                      </div>

                      <div className="flex items-center min-w-0">
                        <AdminTableMeta>
                          {formatDateTime(q.lastQuoteThreadOn || q.lastActivity)}
                        </AdminTableMeta>
                      </div>

                      <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rowKey);
                            }}
                            aria-expanded={!!rowActionsOpen[rowKey]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rowKey] && rowActionsMenuLayout && (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  closeRowActions();
                                  goQuoteDetail(q);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalFilteredCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Quotes;
